// React imports
import { useEffect, useMemo, useState } from "react";

// External library imports
import { Autocomplete, Box, CircularProgress, Paper, Rating, TextField } from "@mui/material";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarQuickFilter,
  GridPagination,
} from "@mui/x-data-grid";
import { ThunkDispatch } from "@reduxjs/toolkit";
import { useDispatch, useSelector } from "react-redux";

// Local imports
import { setInPageLoading, setIsLoading } from "../../store/LoadingSlice";
import { RootState } from "../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
} from "../../store/actions";
import {
  allEmployeeSkiillResponses,
  viewSkillHistory,
  viewActiveUsers,
} from "../../Service/Apis";
import CustomNoRowsOverlay from "../../components/Data/NoRowsOverlayComponent";
import NothingToDisplayComponent from "../../components/Data/NothingToShowComponent";
import {
  AllEmployeeResponsesType,
  EmployeeSkillSetType,
  SkillHistoryType,
  Employeetype,
  SkillHistoryRow,
} from "./Types/SkillTypes";
import { getDataGridPaginationProps } from "utils/paginationConfig";

// Custom Toolbar with Search and Pagination at the top
function CustomToolbarWithPagination() {
  return (
    <GridToolbarContainer
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0.5rem",
        borderBottom: "1px solid rgba(224, 224, 224, 1)",
        flexWrap: "wrap",
        gap: 1,
      }}
    >
      <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        <GridToolbarDensitySelector />
        <GridToolbarExport />
        <GridToolbarQuickFilter sx={{ ml: 2 }} />
      </Box>
      <GridPagination />
    </GridToolbarContainer>
  );
}

const SkillHistory = () => {
  const [data, setData] = useState<SkillHistoryType[]>([]);
  const [employeesData, setEmployeesData] = useState<Employeetype[]>([]);
  const [employee, setEmployee] = useState<Employeetype | null>(null);

  // Redux Hooks
  const dispatch = useDispatch();
  const dispatch2: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const isLoading = useSelector((state: RootState) => state.loading.isLoading);

  const rows = useMemo(() => {
    return data.map((skill, index) => ({
      ...skill,
      index: index + 1,
    }));
  }, [data]);

  const columns: GridColDef[] = [
    {
      field: "index",
      headerName: "Sr. No",
      renderHeader: () => (
        <strong
          className="custom-header"
          style={{ fontSize: 17, textAlign: "center" }}
        >
          Sr No.
        </strong>
      ),
      width: 80,
      align: "center",
      headerAlign: "center",
      headerClassName: "header-background",
    },
    {
      field: "employee_name",
      headerName: "Employee Name",
      renderHeader: () => (
        <strong
          className="custom-header"
          style={{ paddingRight: 2, fontSize: 17, textAlign: "center" }}
        >
          Employee Name
        </strong>
      ),
      width: 200,
      align: "center",
      headerAlign: "center",
      headerClassName: "header-background",
    },
    {
      field: "skill_name",
      headerName: "Skill Name",
      renderHeader: () => (
        <strong
          className="custom-header"
          style={{ fontSize: 17, textAlign: "center" }}
        >
          Skill Name
        </strong>
      ),
      flex: 1,
      minWidth: 420,
      align: "center",
      headerAlign: "center",
      headerClassName: "header-background",
      renderCell: (params: GridRenderCellParams<SkillHistoryRow>) => (
        <Box sx={{ whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.3, textAlign: "left", width: "100%" }}>
          {params.value}
        </Box>
      ),
    },
    {
      field: "previous_avg",
      headerName: " Previous AVG",
      width: 185,
      align: "center",
      headerAlign: "center",
      headerClassName: "header-background",
      renderHeader: () => (
        <strong
          className="custom-header"
          style={{ fontSize: 17, textAlign: "center" }}
        >
          Previous AVG
        </strong>
      ),
      renderCell: (params: GridRenderCellParams<SkillHistoryRow>) => (
        <Rating
          readOnly
          className="rating-color"
          value={params.row.previous_avg ? params.row.previous_avg : 0}
        />
      ),
    },
    {
      field: "current_avg",
      headerName: " Current AVG",
      width: 185,
      align: "center",
      headerAlign: "center",
      headerClassName: "header-background",
      renderHeader: () => (
        <strong
          className="custom-header"
          style={{ fontSize: 17, textAlign: "center" }}
        >
          Current AVG
        </strong>
      ),
      renderCell: (params: GridRenderCellParams<SkillHistoryRow>) => (
        <Rating
          readOnly
          className="rating-color"
          value={params.row.current_avg ? params.row.current_avg : 0}
        />
      ),
    },
  ];

  const handleEmployeeChange = (
    e: React.SyntheticEvent,
    values: Employeetype | null
  ) => {
    setEmployee(values);
  };

  useEffect(() => {
    // API Call get the User Details.
    const fetchSkillHistory = async () => {
      try {
        dispatch(setIsLoading(true));
        const response = await dispatch2(
          fetchData(
            `${viewSkillHistory}${employee ? `?user_id=${employee.id}` : ""}`
          )
        );
        setData(response as SkillHistoryType[]);
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(setIsLoading(false));
      }
    };

    fetchSkillHistory();
  }, [employee]);

  useEffect(() => {
    // API Call get the User Details.
    const fetchUserDetails = async () => {
      try {
        dispatch(setIsLoading(true));
        const response = await dispatch2(fetchData(`${viewActiveUsers}`));
        setEmployeesData(response as Employeetype[]);
      } catch (error) {
        console.log(error);
      } finally {
        dispatch(setIsLoading(false));
      }
    };

    fetchUserDetails();
  }, []);

  if (isLoading) {
    return (
      <>
        {/* Fullscreen Overlay */}
        <Box
          id="fullscreen-overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backdropFilter: "blur(1.5px)", // Adjust the blur intensity as needed
            zIndex: 9999,
          }}
        />
        {/* Loader Container */}
        <Box
          id="loader-container"
          sx={{
            position: "fixed", // Use fixed positioning for the loader container
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
        >
          {/* Loader */}
          <Box
            id="loader"
            sx={{
              position: "relative", // Use relative positioning for the actual loader
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isLoading && <CircularProgress size={50} color="success" />}
          </Box>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box id="main-component" sx={{ mt: 10, mb: 10 }}>
        <Box sx={{ width: "98%", display: "flex", mx: 1, mt: 2 }}>
          <Box sx={{ width: "100%" }}>
            <Box sx={{ mb: 1 }}>
              <Autocomplete
                size="small"
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) =>
                  option.first_name + " " + option.last_name
                }
                value={employee}
                onChange={handleEmployeeChange}
                id="employee-autocomplete"
                options={employeesData}
                sx={{ width: "60%", mx: "auto" }}
                renderInput={(params) => <TextField {...params} />}
              />
            </Box>
            {data.length === 0 ? (
              <Box sx={{ mt: 10 }}>
                <NothingToDisplayComponent />
              </Box>
            ) : (
              <Box sx={{ width: "100%" }}>
                <Paper sx={{ width: "100%", m: 1, p: 2 }}>
                  <DataGrid
                    autoHeight
                    rowHeight={64}
                    getRowHeight={() => 'auto'}
                    {...getDataGridPaginationProps()}
                    disableColumnMenu
                    sx={{
                      width: "100%",
                      height: "100%",
                      "--DataGrid-overlayHeight": "50vh",
                      '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#e8f5e9',
                      },
                      '& .MuiDataGrid-columnHeader': {
                        backgroundColor: '#e8f5e9',
                        color: '#1b5e20',
                      },
                      '& .MuiDataGrid-columnSeparator': {
                        color: '#1b5e20',
                        borderColor: '#e0eee3',
                      },
                    }}
                    pagination
                    rows={rows}
                    columns={columns}
                    disableColumnSelector
                    disableDensitySelector
                    slots={{
                      toolbar: CustomToolbarWithPagination,
                      noRowsOverlay: CustomNoRowsOverlay,
                      noResultsOverlay: CustomNoRowsOverlay,
                    }}
                    slotProps={{
                      toolbar: {
                        showQuickFilter: true,
                        style: { margin: "0.5rem" },
                      },
                    }}
                    getRowId={(row) => row.index}
                  />
                </Paper>
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default SkillHistory;
